import { CommonModule } from '@angular/common';
import { Component, OnInit, HostListener, OnDestroy } from '@angular/core';
import {
  RouterOutlet,
  Router,
  NavigationEnd,
  ActivatedRoute,
} from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageHeaderComponent } from './shared/components/layout/page-header/page-header.component';
import { SideNavComponent } from './shared/components/layout/side-nav/side-nav.component';
import { AuthService } from './core/services/auth.service';
import { UserProfile } from './shared/models';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { NotificationService } from './shared/services/notification.service';

// PrimeNG Components - Import only what you need for better tree-shaking
// These imports are optimized for bundle size
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { SelectModule } from 'primeng/select';

interface City {
  name: string;
  code: string;
}

interface Product {
  name: string;
  category: string;
  price: number;
  status: string;
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    CommonModule,
    FormsModule,
    PageHeaderComponent,
    SideNavComponent,
    // PrimeNG modules - only import what you actually use
    // This helps with tree-shaking and reduces bundle size
    ButtonModule,
    InputTextModule,
    CardModule,
    SelectModule,
    TableModule,
    TagModule,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'niti-frontend';
  private destroy$ = new Subject<void>();

  // Sidebar state
  isSidebarOpen: boolean = true;
  currentRoute: string = 'dashboard';

  // User profile data
  userProfile: UserProfile | null = null;
  greeting: string = '';

  // Authentication state
  isAuthenticated: boolean = false;
  isAuthCheckComplete: boolean = false;
  currentUrl: string = '';

  constructor(
    private router: Router,
    private authService: AuthService,
    private activatedRoute: ActivatedRoute,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    // Initialize current URL from window location first (most reliable on initial load)
    // router.url might be empty or not ready on initial page load, so use window.location
    const initialUrl = window.location.pathname + window.location.search;
    // Prefer window.location if router.url is empty or just '/'
    this.currentUrl =
      this.router.url && this.router.url !== '/' && this.router.url !== ''
        ? this.router.url
        : initialUrl;

    // Listen to authentication state changes FIRST
    this.authService.isAuthenticated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isAuth) => {
        this.isAuthenticated = isAuth;
        this.isAuthCheckComplete = true; // Mark as complete when we get the first value
        if (isAuth) {
          this.initializeUserProfile();
          this.initializeNotifications();
        } else {
          this.userProfile = null;
          this.greeting = '';
        }
      });

    // Initialize authentication state synchronously - but skip if on a public route
    // This prevents redirecting from public routes like reset-password
    if (!this.isPublicRoute(this.currentUrl)) {
      this.checkAuthenticationStatus();
    }

    // Listen to current user changes
    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (user) {
          this.userProfile = {
            name: user.name || user.email || 'User',
            role: user.role || 'User',
            avatar: (user.name || user.email || 'User').charAt(0).toUpperCase(),
          };
          this.greeting = this.getGreeting();
        }
      });

    // Track route changes to update active route
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.currentUrl = event.url;
        this.updateCurrentRoute(event.url);
        // Only check auth status if we're navigating to a protected route
        // Skip auth check for public routes to prevent redirects
        if (this.isProtectedRoute(event.url)) {
          this.checkAuthenticationStatus();
        }
      });

    // Initialize sidebar state based on screen size
    this.initializeSidebarState();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.initializeSidebarState();
  }

  private checkAuthenticationStatus(): void {
    // Check authentication status from the service
    this.isAuthenticated = this.authService.isAuthenticated();

    // If not authenticated and on a protected route, redirect to sign-in
    // Only redirect if we're on a protected route (not a public route)
    if (!this.isAuthenticated && this.isProtectedRoute(this.currentUrl)) {
      this.router.navigate(['/sign-in']);
    }
  }

  private isProtectedRoute(url: string): boolean {
    // Check if the current route is not a public route
    return !this.isPublicRoute(url);
  }

  shouldShowLayout(): boolean {
    // Only show layout if user is authenticated and auth check is complete
    // AND the current route is not a public route
    const isPublicRoute = this.isPublicRoute(this.currentUrl);

    return this.isAuthenticated && this.isAuthCheckComplete && !isPublicRoute;
  }

  private getPathFromUrl(url: string): string {
    // Extract pathname without query parameters or hash
    if (!url) return '';
    return url.split('?')[0].split('#')[0];
  }

  private isPublicRoute(url: string): boolean {
    // Define public routes that should not show layout
    const publicRoutes = ['/sign-in', '/forgot-password', '/reset-password'];

    // Extract pathname without query parameters or hash
    const pathname = this.getPathFromUrl(url);

    // Check if current URL matches any public route or is root
    return pathname === '/' || publicRoutes.includes(pathname);
  }

  private initializeUserProfile(): void {
    if (this.isAuthenticated) {
      const currentUser = this.authService.getCurrentUser();
      if (currentUser) {
        this.userProfile = {
          name: currentUser.name || currentUser.email || 'User',
          role: currentUser.role || 'User',
          avatar: (currentUser.name || currentUser.email || 'User')
            .charAt(0)
            .toUpperCase(),
        };

        // Set greeting
        this.greeting = this.getGreeting();
      }
    } else {
      this.userProfile = null;
      this.greeting = '';
    }
  }

  private initializeNotifications(): void {
    // Clear any existing notifications first to prevent cross-user data leakage
    this.notificationService.clearNotifications();
    // Load notifications when user logs in
    this.notificationService.loadNotificationsOnLogin();
  }

  private getGreeting(): string {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  }

  private initializeSidebarState(): void {
    // On mobile, start with sidebar closed
    if (window.innerWidth < 768) {
      this.isSidebarOpen = false;
    } else {
      this.isSidebarOpen = true;
    }
  }

  private updateCurrentRoute(url: string): void {
    if (url.includes('/dashboard')) {
      this.currentRoute = 'dashboard';
    } else if (url.includes('/letters')) {
      this.currentRoute = 'letters';
    } else if (url.includes('/routing-slip')) {
      this.currentRoute = 'routing-slip';
    } else if (url.includes('/users')) {
      this.currentRoute = 'users';
    } else if (url.includes('/account')) {
      this.currentRoute = 'account';
    } else {
      this.currentRoute = 'dashboard';
    }
  }

  onSidebarToggle(): void {
    this.isSidebarOpen = !this.isSidebarOpen;
  }

  onSidebarClose(): void {
    this.isSidebarOpen = false;
  }

  // Sample data for dropdown
  cities: City[] = [
    { name: 'New York', code: 'NY' },
    { name: 'London', code: 'LDN' },
    { name: 'Paris', code: 'PRS' },
    { name: 'Tokyo', code: 'TKY' },
  ];
  selectedCity: City | undefined;

  // Sample data for table
  products: Product[] = [
    { name: 'Laptop', category: 'Electronics', price: 999, status: 'In Stock' },
    { name: 'Phone', category: 'Electronics', price: 699, status: 'Low Stock' },
    { name: 'Book', category: 'Education', price: 29, status: 'In Stock' },
    {
      name: 'Chair',
      category: 'Furniture',
      price: 199,
      status: 'Out of Stock',
    },
  ];

  getSeverity(status: string): string {
    switch (status) {
      case 'In Stock':
        return 'success';
      case 'Low Stock':
        return 'warn';
      case 'Out of Stock':
        return 'danger';
      default:
        return 'info';
    }
  }
}
